import axios from "axios";

const BASE_URL = `${process.env.REACT_APP_BACKEND_URL}/api/v1/admin`;

// Name of a window event dispatched on an unrecoverable auth failure. Kept
// as a plain browser event (not a direct Redux store import) deliberately —
// this file sits underneath api.js, which users-slice.js imports, which
// store.js pulls in — importing the store back here completes a cycle that
// crashes the production build with a "cannot access before initialization"
// error (works fine in dev, breaks under the minifier's module ordering).
// App.js listens for this event and dispatches clearUserState() instead.
export const FORCED_LOGOUT_EVENT = "medipro:forced-logout";

export const rootClient = axios.create({ baseURL: BASE_URL });
export const publicClient = axios.create({ baseURL: BASE_URL });

// publicClient has no auth/refresh concerns (it's used pre-login), but
// without this it still fails the same way rootClient would without its
// response interceptor: a rejected request surfaces axios's generic
// "Request failed with status code 400" instead of the backend's actual
// { message } body — e.g. login's "Incorrect password."
publicClient.interceptors.response.use(
  (response) => response,
  (error) => Promise.reject(error.response?.data || error)
);

rootClient.interceptors.request.use((config) => {
  const accessToken = localStorage.getItem("accessToken");
  if (accessToken) config.headers.Authorization = `Bearer ${accessToken}`;
  return config;
});

// A refresh that can't be recovered (expired/invalid refresh token, or none
// stored at all) must drop the user back to the login screen immediately —
// otherwise the app keeps rendering the dashboard with dead tokens, every
// request 401s forever, and nothing loads until the user manually logs out.
const forceLogout = () => {
  localStorage.removeItem("accessToken");
  localStorage.removeItem("refreshToken");
  window.dispatchEvent(new Event(FORCED_LOGOUT_EVENT));
};

rootClient.interceptors.response.use(
  (response) => (response?.config?.returnRawResponse ? response : response?.data),
  async (error) => {
    const { response, config: originalRequest } = error;

    if (response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;
      const oldRefreshToken = localStorage.getItem("refreshToken");

      if (oldRefreshToken) {
        try {
          const res = await publicClient.post("/auth/refresh", { refresh_token: oldRefreshToken });
          localStorage.setItem("accessToken", res.data.data.accessToken);
          return rootClient(originalRequest);
        } catch (refreshError) {
          forceLogout();
          return Promise.reject(refreshError.response?.data || refreshError);
        }
      }

      forceLogout();
    }

    return Promise.reject(response?.data || error);
  }
);
